import { Controller } from "@hotwired/stimulus"
import { NavbarVisibility } from "../lib/navbar_visibility.mjs"

// One vertical direction always selects the same top surface: content moving down (or a finger
// moving up) hides the app header and exposes the sticky player; the reverse reveals navigation.
// Touch intent is read directly because scrollY stops changing at the ends of the document.
export default class extends Controller {
  static targets = ["header"];

  connect() {
    this.visibility = new NavbarVisibility(this.#scrollY());
    this.scrollHandler = this.handleScroll.bind(this);
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchMoveHandler = this.handleTouchMove.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);
    this.wheelHandler = this.handleWheel.bind(this);
    this.keyDownHandler = this.handleKeyDown.bind(this);
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    window.addEventListener("touchstart", this.touchStartHandler, { passive: true });
    window.addEventListener("touchmove", this.touchMoveHandler, { passive: true });
    window.addEventListener("touchend", this.touchEndHandler, { passive: true });
    window.addEventListener("touchcancel", this.touchEndHandler, { passive: true });
    window.addEventListener("wheel", this.wheelHandler, { passive: true });
    window.addEventListener("keydown", this.keyDownHandler);
  }

  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler);
    window.removeEventListener("touchstart", this.touchStartHandler);
    window.removeEventListener("touchmove", this.touchMoveHandler);
    window.removeEventListener("touchend", this.touchEndHandler);
    window.removeEventListener("touchcancel", this.touchEndHandler);
    window.removeEventListener("wheel", this.wheelHandler);
    window.removeEventListener("keydown", this.keyDownHandler);
  }

  handleScroll() {
    this.#apply(this.visibility.scroll(this.#scrollY()));
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) return this.visibility.touchEnd(this.#scrollY());

    this.visibility.touchStart(this.#point(event.touches[0]), this.#scrollY());
  }

  handleTouchMove(event) {
    if (event.touches.length !== 1) return this.visibility.touchEnd(this.#scrollY());

    this.#apply(this.visibility.touchMove(this.#point(event.touches[0]), this.#scrollY()));
  }

  handleTouchEnd() {
    this.visibility.touchEnd(this.#scrollY());
  }

  handleWheel(event) {
    this.#apply(this.visibility.navigate(event.deltaY, this.#scrollY()))
  }

  handleKeyDown(event) {
    if (event.defaultPrevented) return
    if (!NAVIGATION_KEYS.has(event.key)) return
    if (event.target.closest?.("input, textarea, select, [contenteditable]")) return

    this.#apply(this.visibility.navigate(this.#keyDelta(event), this.#scrollY()))
  }

  playerPresented() {
    this.#apply(this.visibility.playerPresented(this.#scrollY()))
  }

  playerDismissed() {
    this.#apply(this.visibility.playerDismissed(this.#scrollY()))
  }

  #apply(intent) {
    if (intent === "hide") this.headerTarget.classList.add("app-header--hidden");
    if (intent === "show") this.headerTarget.classList.remove("app-header--hidden");
  }

  #point(touch) {
    return { x: touch.clientX, y: touch.clientY };
  }

  #scrollY() {
    const maximum = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);

    // Safari reports elastic overscroll outside the real document range. Clamping prevents the
    // spring-back from looking like an intentional gesture in the opposite direction.
    return Math.min(Math.max(window.scrollY, 0), maximum);
  }

  #keyDelta(event) {
    if (event.key === "Home" || event.key === "ArrowUp" || event.key === "PageUp") return -1
    if (event.key === " " && event.shiftKey) return -1

    return 1
  }
}

const NAVIGATION_KEYS = new Set([
  "ArrowDown", "ArrowUp", "End", "Home", "PageDown", "PageUp", " "
])
